import { Button, Col, Form, Row, Select, SelectProps } from 'antd';
import TextInput from '../Controls/TextInput';
import {
  Complication,
  ComplicationCategory,
  ComplicationPermissionType,
  ComplicationStatus,
  ComplicationType,
} from '../../../dto/Complication.dto';
import {
  ComplicationSlotType,
  supportedSlotMimeTypes,
  supportedWfPhotoMimeTypes,
  WFComplicationType,
  WFFamilyType,
} from '../../../dto/WatchFace.dto';
import UploadPreview from '../WatchFace/UploadPreview';
import { useEffect, useState } from 'react';
import { REQUIRED_RULE } from '../../../rules/required';

interface ComplicationsFormProps {
  complication?: Complication;
  isEdit: boolean;
  onFinish: (values: any) => Promise<void>;
}

export function ComplicationsForm(props: ComplicationsFormProps) {
  const { complication, isEdit, onFinish } = props;

  const [form] = Form.useForm();
  const [preview, setPreview] = useState<any>([]);
  const [file, setFile] = useState<any>([]);

  useEffect(() => {
    if (!complication) return;

    if (complication.previewUrl) {
      setPreview([
        {
          uid: '-1',
          name: complication.previewId,
          status: 'done',
          url: complication.previewUrl,
        },
      ]);
    }

    if (complication.fileId) {
      setFile([
        {
          uid: '-1',
          name: complication.fileId,
          status: 'done',
          url: complication.fileUrl,
        },
      ]);
    }
  }, [complication]);

  useEffect(() => {
    return () => {
      form.resetFields();
      setPreview([]);
      setFile([]);
    };
  }, []);

  return (
    <Form
      onFinish={onFinish}
      form={form}
      autoComplete='off'
      layout='vertical'
      initialValues={complication}
    >
      <Row>
        <Col span={12}>
          <Form.Item
            name='preview'
            label='Preview'
            rules={[
              {
                required: !preview?.length,
                message: 'Please upload complication preview!',
              },
            ]}
          >
            <UploadPreview
              name='preview'
              supportType={supportedWfPhotoMimeTypes}
              defaultFileList={preview}
            />
          </Form.Item>
        </Col>

        <Col span={12}>
          <Form.Item
            name='file'
            label='File'
            rules={[
              {
                required: !file?.length,
                message: 'Please upload complication file!',
              },
            ]}
          >
            <UploadPreview
              disabled={isEdit}
              name='file'
              supportType={supportedSlotMimeTypes}
              defaultFileList={file}
            />
          </Form.Item>
        </Col>

        <Col span={24}>
          <Row gutter={[20, 20]}>
            <Col span={12}>
              <Form.Item name='position' label='Position'>
                <TextInput placeholder='Complication position' type='number' />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item name='popularityIndex' label='popularityIndex'>
                <TextInput type='number' disabled />
              </Form.Item>
            </Col>
          </Row>
        </Col>

        <Col span={24}>
          <Form.Item name='name' label='Name' rules={[REQUIRED_RULE]}>
            <TextInput />
          </Form.Item>
        </Col>

        <Col span={24}>
          <Form.Item name='slot' label='Slot' rules={[REQUIRED_RULE]}>
            <Select options={enumToSelectOptions(ComplicationSlotType)} />
          </Form.Item>
        </Col>

        <Col span={24}>
          <Form.Item
            name='familyType'
            label='Family Type'
            rules={[REQUIRED_RULE]}
          >
            <Select options={enumToSelectOptions(WFFamilyType)} />
          </Form.Item>
        </Col>

        <Col span={24}>
          <Form.Item name='permissionTypes' label='Permission Types'>
            <Select
              options={enumToSelectOptions(ComplicationPermissionType)}
              mode='multiple'
            />
          </Form.Item>
        </Col>

        <Col span={24}>
          <Form.Item
            name='categories'
            label='Categories'
            rules={[REQUIRED_RULE]}
          >
            <Select
              options={enumToSelectOptions(ComplicationCategory)}
              mode='multiple'
            />
          </Form.Item>
        </Col>

        <Col span={24}>
          <Form.Item name='status' label='Status' rules={[REQUIRED_RULE]}>
            <Select options={enumToSelectOptions(ComplicationStatus)} />
          </Form.Item>
        </Col>

        <Col span={24}>
          <Form.Item name='type' label='Type' rules={[REQUIRED_RULE]}>
            <Select options={enumToSelectOptions(ComplicationType)} />
          </Form.Item>
        </Col>

        <Col span={24}>
          <Form.Item
            name='supportedComplicationTypes'
            label='Supported Complication Types'
            rules={[REQUIRED_RULE]}
          >
            <Select
              options={enumToSelectOptions(WFComplicationType)}
              mode='multiple'
            />
          </Form.Item>
        </Col>
      </Row>

      <Row>
        <Col span={24} style={{ display: 'flex', gap: '10px' }}>
          <Button htmlType='submit'>
            {complication?.id ? 'Update' : 'Create'}
          </Button>
          <Button onClick={onCancel}>Cancel</Button>
        </Col>
      </Row>
    </Form>
  );

  function onCancel() {}
}

function humanizeEnumKey(key: string): string {
  return key
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .toLowerCase() // всё в нижний регистр
    .replace(/\b\w/g, (char) => char.toUpperCase()); // первая буква каждого слова в верхний регистр
}

export function enumToSelectOptions<T extends Record<string, string | number>>(
  enumObj: T
): SelectProps['options'] {
  const isNumericEnum = Object.keys(enumObj).some(
    (key) => typeof enumObj[key as keyof T] === 'number'
  );

  const entries = isNumericEnum
    ? Object.entries(enumObj).filter(([key]) => isNaN(Number(key)))
    : Object.entries(enumObj);

  return entries.map(([key, value]) => ({
    label: humanizeEnumKey(key),
    value: value,
  }));
}
